export const InboxRecommendation = {
  PhaseOut: "phaseOut",
  AddToCollection: "addToCollection",
  // Value kept as "updateActivation" for backward-compat with persisted filter/sort state.
  SetActivation: "updateActivation",
  ReviewActivation: "reviewActivation",
  HandleInErp: "handleInErp",
  ReviewSystemDate: "reviewSystemDate",
} as const

export type InboxRecommendationType = (typeof InboxRecommendation)[keyof typeof InboxRecommendation]

import type { NpiTabContext } from "~/constants/productTable"

export const actionDisabledTooltips = {
  activationDate: {
    hidden: "Hidden variants cannot have their activation date changed",
    phasedOut: "Phased out variants cannot have their activation date changed",
  },
  addToCollection: {
    hidden: "Hidden variants must be unhidden before adding to a collection",
    phasedOut: "Phased out variants must be reactivated before adding to a collection",
  },
  phaseOut: {
    hidden: "Hidden variants cannot be phased out",
  },
  reactivate: {
    hidden: "Hidden variants must be unhidden before reactivating",
  },
  phasingOut: {
    hidden: "Hidden variants cannot be set to phasing out",
    phasedOut: "Phased out variants must be reactivated before setting phasing out",
    phasingOut: "These variants are already phasing out",
  },
  hide: {
    phasedOut: "Phased out variants cannot be hidden",
  },
} satisfies Record<string, Partial<Record<NpiTabContext, string>>>

export function getActionDisabledTooltip(
  action: keyof typeof actionDisabledTooltips,
  tabContext: NpiTabContext,
): string | undefined {
  const map = actionDisabledTooltips[action] as Partial<Record<NpiTabContext, string>>
  return map[tabContext]
}

function removalSubject(inCollectionCount?: number, selectedCount?: number): string {
  if (inCollectionCount === undefined || inCollectionCount === selectedCount) {
    return inCollectionCount === 1 ? "This variant is" : "These variants are"
  }
  return `${inCollectionCount} of them ${inCollectionCount === 1 ? "is" : "are"}`
}

/** `inCollectionCount` below `selectedCount` scopes the sentence to part of a selection ("2 of them are in …"). */
export function hideNeedsCollectionRemovalMessage(
  collectionNames: string[],
  inCollectionCount?: number,
  selectedCount?: number,
): string {
  const one = inCollectionCount === 1
  const subject = removalSubject(inCollectionCount, selectedCount)
  const target = collectionNames.length === 1 ? "that collection" : "those collections"
  return `${subject} in ${collectionNames.join(", ")}. To hide ${one ? "it" : "them"}, ${one ? "it" : "they"} also ${one ? "has" : "have"} to be removed from ${target}. Unhiding later does not add ${one ? "it" : "them"} back.`
}

export function hideBlockedByErpMessage(collectionNames: string[] = []): string {
  if (collectionNames.length === 0) {
    return "Variants in an ERP collection cannot be hidden here. Remove them from the collection in your ERP first."
  }
  const source = collectionNames.length === 1 ? "That collection comes" : "Those collections come"
  return `Variants in ${collectionNames.join(", ")} cannot be hidden here. ${source} from your ERP, so remove the variants there first.`
}

export const ACTIVATION_STALE_MONTHS = 6

/**
 * Client mirror of the SQL `needsActivation`. `activatedFrom` is the date To review judges: NPI rows'
 * `settings.reviewActivatedFrom`; SKU-store rows' `settings.activatedFrom` is already the date in use.
 */
export function variantNeedsActivation(
  settings:
    | { productType?: string | null; isNpi?: boolean | null; hasSalesBasedForecast?: boolean }
    | undefined,
  activatedFrom: Date | string | null | undefined,
): boolean {
  if (settings?.productType?.toLowerCase() !== "noos") return false
  if (!settings.isNpi) return false
  // May over-report vs SQL needsActivation (no hasForecastVariant here); the SQL field is authoritative.
  if (!activatedFrom) return true
  if (settings.hasSalesBasedForecast) return false
  const date = activatedFrom instanceof Date ? activatedFrom : new Date(activatedFrom)
  const staleCutoff = new Date()
  staleCutoff.setUTCMonth(staleCutoff.getUTCMonth() - ACTIVATION_STALE_MONTHS)
  return date.getTime() < staleCutoff.getTime()
}

export const NOOS_REFERENCE_BASIS = "noosReference"

export const NOOS_BASES = ["firstYear", "lastYear", "specificYear", "yearlyAverage"] as const
export type NoosBasis = (typeof NOOS_BASES)[number]
export const isNoosBasis = (basis: string | null | undefined): basis is NoosBasis =>
  NOOS_BASES.includes(basis as NoosBasis)

export type ForecastBasis = NoosBasis | "collection"
export type StoredForecastBasis = ForecastBasis | typeof NOOS_REFERENCE_BASIS
